<!--
    Notification List

    <NotificationList
      :notification-list="..."      # MANDATORY - list of notifications
      :has-next-page="..."          # OPTIONAL - is there a next page after the current page
      :max-container-height="..."   # OPTIONAL - max height of wrapper
      :min-container-height="..."   # OPTIONAL - min height of wrapper

      @update-page="..."      - fired on scroll for page update
      @close
    </NotificationList>
-->
<script setup lang="ts">
/* COMPONENTS */
import { ALLoader } from '@af-lib/vue-ui';
import { useRouter } from 'vue-router';

import { useDeleteNotification, useUpdateNotification } from '@/api/vue-query/notifications/notifications.mutations';
import type { MeNotifications } from '@/types/api/me/me';
/* TYPES */
import type { Notification } from '@/types/notifications/notification';

/* CONFIG */
import { getNotifConfig } from './notificationLayout.config';
import NotificationLayout from './NotificationLayout.vue';

/* PROPS */
type Props = {
  notificationList: Notification[];
  hasNextPage?: boolean;
  maxContainerHeight?: string;
  minContainerHeight?: string;
};
const props = defineProps<Props>();
const router = useRouter();

/* EMITS */
const emits = defineEmits<{
  (e: 'update:page'): void;
  (e: 'close'): void;
}>();

/* FUNCTIONS */
/**
 * Function called when the bottom loader is visible to trigger a call for the next page
 *
 * @param {Boolean} isVisible : true if the element is visible
 */
function onBottomReached(isVisible) {
  if (isVisible) {
    emits('update:page');
  }
}

/* API */
const { mutate: deleteNotifs, isPending: deletingNotif } = useDeleteNotification();
const { mutate: updateNotif } = useUpdateNotification();

/**
 * From received notification clicked, redirect to the right external or internal URL
 *
 * @param {Notification} notification Notification that triggers a redirection
 */
function notificationRedirection(notification: Notification) {
  // Get redirection link if available on click
  const { link, route } = getNotifConfig(notification.type);

  if (link) {
    window.location.assign(link(notification));
  }
  // Phoenix internal route
  if (route) {
    router.push(route(notification));
  }
}

function deleteNotif(uuid: string) {
  deleteNotifs([uuid]);
}

/**
 * If a notification is clicked, set new status
 */
function onUpdateNotification(notification: MeNotifications) {
  // Set status of notif inside list to clicked
  if (notification.status !== 'clicked') {
    updateNotif({ uuids: [notification.uuid], status: 'clicked' });
  }
  notificationRedirection(notification);

  // Close menu
  emits('close');
}
</script>

<template>
  <!-- Notification list infinite -->
  <v-list id="notification-list-element">
    <template v-for="notification in props.notificationList" :key="notification.uuid">
      <!-- V for on template to check if the conrresponding type exist -->
      <v-list-item
        v-if="getNotifConfig(notification.type)"
        :disabled="deletingNotif"
        :class="[
          `notification-item-pointer notification-item${notification.status !== 'clicked' ? '-unread' : ''}`,
          'pa-0'
        ]"
        :aria-label="getNotifConfig(notification.type)?.ariaLabel(notification)"
        @click.stop="onUpdateNotification(notification)"
      >
        <NotificationLayout
          :notification="notification"
          :notificationConfig="getNotifConfig(notification.type)"
          @click:trash="deleteNotif(notification.uuid)"
        ></NotificationLayout>
        <v-divider></v-divider>
      </v-list-item>
    </template>
    <!-- Loader when fetching page -->
    <div v-if="hasNextPage && props.notificationList.length !== 0" class="d-flex justify-center py-2">
      <ALLoader v-intersect.quiet="onBottomReached" size="s" />
    </div>
  </v-list>
</template>

<style scoped lang="scss">
#notification-list-element {
  min-height: calc(v-bind('props.minContainerHeight') - (65px + 54px)); // height title + height uncount banner
  max-height: calc(v-bind('props.maxContainerHeight') - (65px + 54px)); // height title + height uncount banner
}
.notification-item-pointer {
  cursor: pointer;
}
.notification-item-unread {
  background-color: rgb(var(--v-theme-notificationUnreadBg));
  &:hover {
    background-color: rgb(var(--v-theme-notificationUnreadBgHover));
  }
}
.notification-item:hover {
  background-color: rgba(var(--v-theme-notificationBgHover));
}
</style>
